import { useCallback, useEffect, useState } from 'react';

import Request from '../../../util/Api';
import ShipmentFormModal from './ShipmentFormModal';

import { Badge, Button, DownloadIcon, Icon, Pane, Spinner, Table, Text, toaster } from 'evergreen-ui';

const TYPE_LABELS = { main: 'Principal', extra: 'Suplimentar', exchange: 'La schimb' };

const ERROR_MESSAGES = {
    PACKAGES_REQUIRED: 'Adauga cel putin un colet.',
    PROVIDER_NOT_SUPPORTED: 'Aceasta optiune este disponibila doar pentru Sameday (livrare interna).',
    AWB_CREATE_FAILED: 'Generarea AWB-ului a esuat la curier. Reincearca.',
    CANNOT_CANCEL_MAIN: 'Coletul principal se anuleaza prin anularea comenzii.',
    LABEL_UNAVAILABLE: 'Eticheta nu este disponibila pentru acest colet.'
};

const showError = (e) => {
    const code = e && e.message ? e.message : e;
    // The API attaches the raw courier rejection on `details` for AWB failures;
    // log it so an operator can read why Sameday refused the AWB from devtools.
    if (e && e.details) console.error('Shipment error details:', e.details);
    toaster.danger(ERROR_MESSAGES[code] || 'Eroare: ceva nu a mers bine.');
};

export default function OrderShipmentsPanel(props) {
    const orderId = props.order.id;
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, mode: 'extra' });

    const isDelivery = ['delivery', 'delivery-locker'].includes(props.order.delivery);
    // Swap is Sameday domestic only.
    const isDomestic = !props.order.deliveryCountry || props.order.deliveryCountry === 'RO';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Request.get('/v2/orders/[id]/shipments', { id: orderId });
            setShipments((res && res.data) || []);
        } catch (e) {
            showError(e);
        }
        setLoading(false);
    }, [orderId]);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async (values) => {
        const path = modal.mode === 'exchange'
            ? '/v2/orders/[id]/shipments/exchange'
            : '/v2/orders/[id]/shipments/extra';
        const body = { packages: values.packages, pickupPoint: values.pickupPoint };
        if (modal.mode !== 'exchange') body.cod = Number(values.cod) || 0;

        try {
            await Request.post(path, { id: orderId }, null, body);
            toaster.success('AWB generat!');
            load();
        } catch (e) {
            showError(e);
        }
    };

    const handleCancel = async (shipmentId) => {
        try {
            await Request.post('/v2/order-shipments/[id]/cancel', { id: shipmentId }, null, {});
            toaster.success('Colet anulat.');
            load();
        } catch (e) {
            showError(e);
        }
    };

    const handleDownload = async (shipmentId, format = 'A4') => {
        try {
            const res = await Request.get('/v2/order-shipments/[id]/label', { id: shipmentId }, { format });
            const a = document.createElement('A');
            a.href = res.data;
            a.download = 'awb-' + shipmentId + '-' + format + '.pdf';
            document.body.appendChild(a);
            a.click();
        } catch (e) {
            showError(e);
        }
    };

    return <Pane width="100%">
        <ShipmentFormModal
            open={modal.open}
            mode={modal.mode}
            order={props.order}
            onClose={() => setModal({ ...modal, open: false })}
            onSubmit={handleCreate}
        />

        {isDelivery ?
            <Pane marginBottom={16}>
                <Button type="button" intent="success" marginRight={10} onClick={() => setModal({ open: true, mode: 'extra' })}>
                    Trimite colet suplimentar
                </Button>
                {isDomestic ?
                    <Button type="button" onClick={() => setModal({ open: true, mode: 'exchange' })}>
                        Colet la schimb
                    </Button> : null}
            </Pane>
        : <Text color="muted">Comanda fara livrare prin curier.</Text>}

        {loading ? <Spinner size={24} /> :
            <Table>
                <Table.Head>
                    <Table.TextHeaderCell>Tip</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Curier</Table.TextHeaderCell>
                    <Table.TextHeaderCell>AWB</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Status</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Ramburs</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Cost</Table.TextHeaderCell>
                    <Table.TextHeaderCell flexBasis={260} flexGrow={0}>Actiuni</Table.TextHeaderCell>
                </Table.Head>
                <Table.Body>
                    {shipments.length === 0 ?
                        <Table.Row>
                            <Table.TextCell>Nu exista expedieri inca.</Table.TextCell>
                        </Table.Row> : null}
                    {shipments.map(s => (
                        <Table.Row key={s.id}>
                            <Table.TextCell>
                                {TYPE_LABELS[s.type] || s.type}
                                {s.cancelledAt ? <Badge color="red" marginLeft={6}>Anulat</Badge> : null}
                            </Table.TextCell>
                            <Table.TextCell>{s.provider}</Table.TextCell>
                            <Table.TextCell>{s.awbNumber || '-'}</Table.TextCell>
                            <Table.TextCell>{s.deliveryStatus || '-'}</Table.TextCell>
                            <Table.TextCell>{s.cod}</Table.TextCell>
                            <Table.TextCell>{s.cost != null ? s.cost : '-'}</Table.TextCell>
                            <Table.Cell flexBasis={260} flexGrow={0}>
                                {s.awbNumber ? <>
                                    <Button type="button" size="small" marginRight={4} onClick={() => handleDownload(s.id, 'A4')}>
                                        <Icon icon={DownloadIcon} marginRight={3} /> A4
                                    </Button>
                                    <Button type="button" size="small" marginRight={4} onClick={() => handleDownload(s.id, 'A6')}>
                                        <Icon icon={DownloadIcon} marginRight={3} /> A6
                                    </Button>
                                </> : null}
                                {s.type !== 'main' && !s.cancelledAt ?
                                    <Button type="button" size="small" intent="danger" onClick={() => handleCancel(s.id)}>
                                        Anuleaza
                                    </Button> : null}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>}
    </Pane>;
}
